/**
 * Reusable typeahead for searching linked Discord/in-game users.
 *
 * Wraps the `/api/admin/users/search` endpoint with sensible debouncing,
 * keyboard navigation (↑ / ↓ / Enter / Esc), and a clean dropdown UI.
 *
 * Use it anywhere an admin form needs to identify a target by Discord
 * display name or in-game character name:
 *
 *   <UserSearchInput
 *     value={discordId}
 *     onPick={(row) => {
 *       setDiscordId(row.discord_id);
 *       setPlayerUid(row.player_userid);
 *     }}
 *     placeholder="Type Discord name or in-game name…"
 *     scope="discord_id"       // what the bare text input represents
 *     serverName={server}      // optional — restrict to one server
 *     dataTestid="give-search"
 *   />
 *
 * The `value` prop is the canonical scalar the form binds to (e.g.
 * discord_id or player_userid). On pick we both fire `onPick(row)` so
 * the parent can pull whatever other fields it needs.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEBOUNCE_MS = 150;

export function UserSearchInput({
  value = "",
  onChange,
  onPick,
  placeholder = "Type a Discord name or in-game name…",
  serverName = null,
  scope = "discord_id", // what `value` stores; affects display only
  dataTestid = "user-search",
  className = "input",
  disabled = false,
}) {
  const [text, setText] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Keep the visible text in sync when the parent resets the canonical value
  useEffect(() => {
    setText(value || "");
  }, [value]);

  // Debounced search — runs on every keystroke after a 150 ms idle.
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const q = text.trim();
        const params = new URLSearchParams({ q, limit: "12" });
        if (serverName) params.set("server_name", serverName);
        const { data } = await api.get(`/admin/users/search?${params.toString()}`);
        setItems(data?.items || []);
        setActive(0);
      } catch {
        setItems([]);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [text, serverName, open]);

  // Click outside to close
  useEffect(() => {
    function onDoc(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(row) {
    if (!row) return;
    setItems([]);
    setOpen(false);
    setActive(-1);
    // Echo the right scalar back into the visible input so the admin can
    // see what got selected (Discord name reads more naturally than an id).
    setText(row.discord_username || row.player_name || row.discord_id || "");
    onChange?.(scope === "player_userid" ? row.player_userid : row.discord_id);
    onPick?.(row);
  }

  function onKeyDown(e) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && items[active]) pick(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          // Free-text edits still flow back so the parent state stays
          // user-controlled (lets an admin paste a raw discord_id).
          onChange?.(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        data-testid={dataTestid}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-[var(--teal-soft)] bg-[#0c0510] shadow-2xl"
          style={{ boxShadow: "0 12px 32px rgba(0,229,255,0.18)" }}
          data-testid={`${dataTestid}-results`}
        >
          {items.map((row, idx) => {
            const isActive = idx === active;
            return (
              <button
                key={`${row.discord_id}-${row.server_name}`}
                type="button"
                onMouseEnter={() => setActive(idx)}
                onClick={() => pick(row)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs transition-colors ${
                  isActive
                    ? "bg-[rgba(0,229,255,0.10)] text-[var(--bone)]"
                    : "text-[var(--muted)] hover:text-[var(--bone)]"
                }`}
                data-testid={`${dataTestid}-row`}
              >
                {row.avatar_url ? (
                  <img
                    src={row.avatar_url}
                    alt=""
                    className="w-5 h-5 rounded-full shrink-0"
                  />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-white/10 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    <span className="text-[var(--bone)]">
                      {row.discord_username || row.player_name || "Unknown"}
                    </span>
                    {row.discord_username && row.player_name && (
                      <span className="text-[var(--muted)]"> · {row.player_name}</span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-[var(--muted)] truncate">
                    {row.server_name?.replace(/Chillet's /, "")} ·{" "}
                    {scope === "player_userid"
                      ? row.player_userid
                      : row.discord_id}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
